import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import StickerAssetEditModal from './StickerAssetEditModal';
import { formatPackStatus } from './stickerConstants';

const TABLE_COLUMNS = ['預覽', 'Emote Code', '名稱', '狀態', '排序', '操作'];

export default function StickerAssetPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const packId = useMemo(() => {
    const raw = searchParams.get('packId');
    return raw ? Number(raw) : null;
  }, [searchParams]);

  const [pack, setPack] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  async function load() {
    if (!packId) {
      setError('缺少 packId');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [packData, stickers] = await Promise.all([
        adminApi.getImStickerPack(packId),
        adminApi.listImStickers(packId),
      ]);
      setPack(packData);
      setRecords(stickers || []);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packId]);

  function openCreate() {
    setEditId(null);
    setEditOpen(true);
    setMessage('');
  }

  function openEdit(row) {
    setEditId(row.id);
    setEditOpen(true);
    setMessage('');
  }

  function handleSaved(isEdit) {
    setMessage(isEdit ? '修改成功' : '新增成功');
    load();
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>
          貼圖管理
          {pack ? ` · ${pack.name}` : ''}
        </h2>
        <div className="section-header__actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/system/sticker-manage/pack')}
          >
            返回包列表
          </button>
          <button type="button" className="btn btn-primary" onClick={openCreate} disabled={!packId}>
            新增貼圖
          </button>
        </div>
      </div>

      {pack ? (
        <p className="table-meta">
          Pack #{pack.id} · {formatPackStatus(pack.status)} · Access {pack.accessType}
        </p>
      ) : null}

      {message ? <p className="form-message form-message--success">{message}</p> : null}
      {error ? <p className="form-message form-message--error">{error}</p> : null}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  載入中…
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  無資料
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td>
                    {row.objectKey ? (
                      <img
                        src={resolveAdminMediaUrl(row.objectKey)}
                        alt=""
                        style={{ width: 48, height: 48, objectFit: 'contain' }}
                      />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <code>{row.emoteCode}</code>
                  </td>
                  <td>{row.name || '—'}</td>
                  <td>{formatPackStatus(row.status)}</td>
                  <td>{row.sort}</td>
                  <td>
                    <button type="button" className="btn btn-link" onClick={() => openEdit(row)}>
                      編輯
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <StickerAssetEditModal
        open={editOpen}
        packId={packId}
        stickerId={editId}
        onClose={() => setEditOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
}
